import * as path from 'path';
import fs from 'fs-extra';
import { Plugin } from 'vite';

// Helper to only write files when content changes to avoid unnecessary HMR triggers
async function writeFileIfChanged(filePath: string, content: string): Promise<boolean> {
	try {
		const exists = await fs.pathExists(filePath);
		if (exists) {
			const existingContent = await fs.readFile(filePath, 'utf-8');
			if (existingContent === content) {
				return false; // No change needed
			}
		}
		await fs.writeFile(filePath, content, 'utf-8');
		return true; // File was written
	} catch (error) {
		// If there's an error reading, just write the file
		await fs.writeFile(filePath, content, 'utf-8');
		return true;
	}
}

export interface AssetConfig {
	publicDir?: string;
	assetsDir?: string;
	staticCopy?: boolean;
	generateHelpers?: boolean;
}

export function createAssetPlugin(cwd: string, config: AssetConfig = {}): Plugin {
	const publicDir = config.publicDir || 'public';
	const assetsDir = config.assetsDir || 'src/assets';
	const publicPath = path.join(cwd, publicDir);
	const assetsPath = path.join(cwd, assetsDir);

	return {
		name: 'frame-assets',
		async buildStart() {
			if (config.generateHelpers !== false) {
				await generateAssetHelpers(cwd, assetsPath);
			}
		},
		configureServer(server) {
			// Watch for changes in assets directory
			if (fs.existsSync(assetsPath)) {
				server.watcher.add(assetsPath);
			}

			// Watch for configuration changes
			const configFiles = ['frame.config.js', 'frame.config.mjs', 'frame.config.ts'];

			configFiles.forEach(configFile => {
				const configPath = path.join(cwd, configFile);
				if (fs.existsSync(configPath)) {
					server.watcher.add(configPath);
				}
			});

			// Handle file changes (both assets and config)
			server.watcher.on('change', async (file) => {
				// Handle config file changes
				if (configFiles.some(cf => file.endsWith(cf))) {
					console.log('[Frame Assets] Configuration changed, regenerating asset helpers...');

					// Reload the configuration from the file
					const { ConfigLoader } = await import('../config/ConfigLoader.js');
					const configLoader = new ConfigLoader(cwd);
					const frameConfig = await configLoader.load();
					const updatedConfig = frameConfig.assets || {};

					// Get updated directories
					const updatedPublicDir = updatedConfig.publicDir || 'public';
					const updatedAssetsDir = updatedConfig.assetsDir || 'src/assets';
					const updatedAssetsPath = path.join(cwd, updatedAssetsDir);

					// Regenerate helpers with updated config
					if (updatedConfig.generateHelpers !== false) {
						await generateAssetHelpers(cwd, updatedAssetsPath);
					}
					return;
				}

				// Handle asset file changes
				if (file.includes(assetsDir)) {
					console.log('[Frame Assets] Assets changed, regenerating helpers...');
					if (config.generateHelpers !== false) {
						await generateAssetHelpers(cwd, assetsPath);
					}
				}
			});
		},
	};
}

async function generateAssetHelpers(cwd: string, assetsPath: string): Promise<void> {
	const frameDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(frameDir);

	// Scan for assets
	const assets = await scanAssets(assetsPath, assetsPath);

	// Generate asset manifest
	const manifestContent = generateAssetManifest(assets);
	const manifestPath = path.join(frameDir, 'assets.ts');
	await writeFileIfChanged(manifestPath, manifestContent);

	// Generate helper functions
	const helpersContent = generateAssetHelpers_content();
	const helpersPath = path.join(frameDir, 'asset-helpers.ts');
	const wasWritten = await writeFileIfChanged(helpersPath, helpersContent);

	if (wasWritten) {
		console.log('[Frame Assets] Asset helpers generated at:', helpersPath);
	}
}

async function scanAssets(currentPath: string, rootPath: string): Promise<AssetInfo[]> {
	if (!fs.existsSync(currentPath)) {
		return [];
	}

	const assets: AssetInfo[] = [];
	const files = await fs.readdir(currentPath, { withFileTypes: true });

	for (const file of files) {
		const filePath = path.join(currentPath, file.name);

		if (file.isDirectory()) {
			// Recursively scan subdirectories
			const subAssets = await scanAssets(filePath, rootPath);
			assets.push(...subAssets);
		} else {
			// Calculate relative path from root assets directory
			const relativePath = path.relative(rootPath, filePath);
			const assetInfo: AssetInfo = {
				name: file.name,
				path: relativePath,
				fullPath: filePath,
				type: getAssetType(file.name),
				size: (await fs.stat(filePath)).size,
			};
			assets.push(assetInfo);
		}
	}

	return assets;
}

function getAssetType(filename: string): AssetType {
	const ext = path.extname(filename).toLowerCase();

	if (['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.avif'].includes(ext)) {
		return 'image';
	}
	if (['.mp4', '.webm', '.ogv', '.mov'].includes(ext)) {
		return 'video';
	}
	if (['.mp3', '.wav', '.ogg', '.flac', '.aac'].includes(ext)) {
		return 'audio';
	}
	if (['.woff', '.woff2', '.ttf', '.otf', '.eot'].includes(ext)) {
		return 'font';
	}
	if (['.css', '.scss', '.sass', '.less'].includes(ext)) {
		return 'style';
	}

	return 'other';
}

function generateAssetManifest(assets: AssetInfo[]): string {
	const assetEntries = assets.map(asset => {
		const key = asset.path.replace(/[^a-zA-Z0-9]/g, '_');
		return `  ${key}: {
    name: '${asset.name}',
    path: '${asset.path}',
    type: '${asset.type}',
    size: ${asset.size},
    url: () => new URL('../assets/${asset.path}', import.meta.url).href,
  }`;
	}).join(',\n');

	return `// Auto-generated asset manifest - do not edit manually
export type AssetType = 'image' | 'video' | 'audio' | 'font' | 'style' | 'other';

export interface AssetInfo {
  name: string;
  path: string;
  type: AssetType;
  size: number;
  url: () => string;
}

export const assets = {
${assetEntries}
} as const;

export type AssetKey = keyof typeof assets;
`;
}

function generateAssetHelpers_content(): string {
	return `// Auto-generated asset helpers - do not edit manually
import { assets, AssetKey, AssetInfo } from './assets';

/**
 * Get the URL for an asset by its key
 */
export function getAssetUrl(key: AssetKey): string {
  return assets[key].url();
}

/**
 * Get asset information by its key
 */
export function getAssetInfo(key: AssetKey): AssetInfo {
  return assets[key];
}

/**
 * Import an image asset with URL
 */
export function importImage(path: string): string {
  return new URL(\`../assets/\${path}\`, import.meta.url).href;
}

/**
 * Get all assets of a specific type
 */
export function getAssetsByType(type: AssetInfo['type']): AssetInfo[] {
  return Object.values(assets).filter(asset => asset.type === type);
}

/**
 * Preload an asset
 */
export function preloadAsset(key: AssetKey): void {
  const asset = assets[key];
  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = asset.url();
  
  if (asset.type === 'image') {
    link.as = 'image';
  } else if (asset.type === 'video') {
    link.as = 'video';
  } else if (asset.type === 'audio') {
    link.as = 'audio';
  } else if (asset.type === 'font') {
    link.as = 'font';
    link.crossOrigin = 'anonymous';
  } else if (asset.type === 'style') {
    link.as = 'style';
  }
  
  document.head.appendChild(link);
}

/**
 * Check if an asset exists
 */
export function assetExists(key: string): key is AssetKey {
  return key in assets;
}
`;
}

interface AssetInfo {
	name: string;
	path: string;
	fullPath: string;
	type: AssetType;
	size: number;
}

type AssetType = 'image' | 'video' | 'audio' | 'font' | 'style' | 'other'; 